import { useFela } from 'react-fela';
import { FormattedMessage } from 'react-intl';

import { CopyButton, PasswordVisualisation, Text } from '@/modules/ui';
import { hasReadPermission } from '@/utilities/permission';
import { isEmptyOrUndefined } from '@/utilities/string';

import type { SecretType } from '../../../constants';
import type { Secret } from '../../../types';
import { buildDatabaseConnectionUrl, resolveDatabaseScheme } from '../../../utilities';
import { SecretFieldSection } from '../../SecretFieldSection';
import { EmptyContentGuard } from '../EmptyContentGuard';
import * as felaRules from './DatabaseCredentialsSections.rules';

export interface DatabaseCredentialsSectionsProps {
    secret: Extract<Secret, { type: SecretType.DatabaseCredentials }>;
}

export const DatabaseCredentialsSections = ({ secret }: DatabaseCredentialsSectionsProps) => {
    const { userPermissions, databaseName, username, password, server, port, databaseType, sensitiveFields } = secret;

    const { css } = useFela();

    const hasRead = hasReadPermission(userPermissions);

    const schemeResolution = resolveDatabaseScheme(databaseType);
    const connectionUrl = buildDatabaseConnectionUrl(secret);

    return (
        <EmptyContentGuard>
            {!isEmptyOrUndefined(databaseName) && (
                <SecretFieldSection heading={<FormattedMessage id='secret.databaseName.label' />}>
                    <Text>{databaseName}</Text>
                </SecretFieldSection>
            )}

            {!isEmptyOrUndefined(username) && (
                <SecretFieldSection heading={<FormattedMessage id='secret.username.label' />}>
                    <CopyButton text={username} />
                </SecretFieldSection>
            )}

            {sensitiveFields?.includes('password') && (
                <SecretFieldSection heading={<FormattedMessage id='secret.password.label' />}>
                    <CopyButton type='password' text={password!} disabled={!hasRead}>
                        <PasswordVisualisation password={password!} />
                    </CopyButton>
                </SecretFieldSection>
            )}

            {!isEmptyOrUndefined(server) && (
                <SecretFieldSection heading={<FormattedMessage id='secret.server.label' />}>
                    <Text>{server}</Text>
                </SecretFieldSection>
            )}

            {!isEmptyOrUndefined(port) && (
                <SecretFieldSection heading={<FormattedMessage id='secret.port.label' />}>
                    <Text>{port}</Text>
                </SecretFieldSection>
            )}
            {!isEmptyOrUndefined(databaseType) && (
                <SecretFieldSection heading={<FormattedMessage id='secret.databaseType.label' />}>
                    <Text>{databaseType}</Text>
                </SecretFieldSection>
            )}

            {schemeResolution.outcome === 'resolved' && connectionUrl && (
                <SecretFieldSection heading={<FormattedMessage id='secret.connectionUrl.label' />}>
                    <CopyButton
                        type='secureTextArea'
                        text={connectionUrl}
                        disabled={!hasRead}
                        mask={
                            <span className={css(felaRules.connectionUrlMask)}>
                                <FormattedMessage id='secret.connectionUrl.mask' values={{ scheme: schemeResolution.scheme }} />
                            </span>
                        }
                    />
                </SecretFieldSection>
            )}
        </EmptyContentGuard>
    );
};
